import { Linkable } from '@/components/blocks/Linkable';
import { drawerSX } from '@/components/blocks/ProductDetails/styles/availability';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useStockBalance } from '@/data/Content/StockBalance';
import { useLocalization } from '@/data/Localization';
import { ContentContext } from '@/data/context/content';
import CircleTwoToneIcon from '@mui/icons-material/CircleTwoTone';
import CloseIcon from '@mui/icons-material/Close';
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import {
	Box,
	Button,
	CircularProgress,
	Divider,
	Drawer,
	IconButton,
	Paper,
	Stack,
	Typography,
	colors,
} from '@mui/material';
import { FC, useContext, useState } from 'react';

export const ProductDetailsAvailability: FC = () => {
	const [stockDrawerState, setStockDrawerState] = useState(false);

	const localization = useLocalization('CommerceEnvironment');
	const prodLoc = useLocalization('productDetail');
	const { product } = useContext(ContentContext) as ReturnType<typeof useProductDetails>;
	const availability = useStockBalance(product?.id ?? '');

	const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
		if (
			event.type === 'keydown' &&
			((event as React.KeyboardEvent).key === 'Tab' ||
				(event as React.KeyboardEvent).key === 'Shift')
		) {
			return;
		}

		setStockDrawerState(open);
	};

	return (
		<>
			<Stack spacing={0.5}>
				<Typography variant="body2">{prodLoc.Availability.t()}</Typography>
				{availability.isLoading ? (
					<Paper variant="outlined">
						<Box p={2}>
							<Stack direction={'row'} spacing={1}>
								<StoreOutlinedIcon />
								<Stack gap={0.5}>
									<Typography fontSize="large" fontWeight={700} textTransform={'uppercase'}>
										{localization.inventoryStatus.Store.t()}
									</Typography>
									<Stack direction={'row'} gap={1} alignItems={'center'}>
										{localization.inventoryStatus.Loading.t()}
										<CircularProgress size={'1rem'} disableShrink />
									</Stack>
								</Stack>
							</Stack>
						</Box>
					</Paper>
				) : availability === undefined || availability.stock === undefined ? (
					<Typography data-testid="not-available" id="not-available">
						{localization.inventoryStatus.NA.t()}
					</Typography>
				) : (
					<Box
						data-testid={`${availability?.stock?.partNumber} in ${availability.storeName}`}
						id={`${availability?.stock?.partNumber} in ${availability.storeName}`}
					>
						<Paper variant="outlined">
							<Box p={2}>
								<Stack spacing={1} divider={<Divider orientation="vertical" flexItem />}>
									<Stack direction={'row'} spacing={1}>
										<WarehouseOutlinedIcon />
										<Butik stockBalanceStore={availability} key={availability.storeName} />
									</Stack>
									<Divider />
									<Stack direction={'row'} spacing={1}>
										<StoreOutlinedIcon />
										<Stack spacing={0.5}>
											<Typography fontSize="large" fontWeight={700} textTransform={'uppercase'}>
												{localization.inventoryStatus.Store.t()}
											</Typography>
											<Button onClick={toggleDrawer(true)} variant="inline">
												{localization.inventoryStatus.StoreStock.link.t()}
											</Button>
										</Stack>
									</Stack>
								</Stack>
							</Box>
						</Paper>
					</Box>
				)}
			</Stack>

			<Drawer
				PaperProps={{
					sx: { minWidth: 300 },
				}}
				anchor={'right'}
				open={stockDrawerState}
				onClose={toggleDrawer(false)}
			>
				<Stack>
					<Stack sx={drawerSX} direction={'row'} justifyContent={'space-between'}>
						<Box pt={10} pb={2} px={4}>
							<Typography
								fontSize="x-large"
								fontWeight={700}
								sx={{ textTransform: 'uppercase', borderBottom: `2px solid` }}
							>
								{localization.inventoryStatus.StoreStock.title.t()}
							</Typography>
						</Box>
						<Box py={1} px={1}>
							<IconButton onClick={toggleDrawer(false)} aria-label="close">
								<CloseIcon />
							</IconButton>
						</Box>
					</Stack>
					{availability.stores?.length === 0 ? (
						<Box py={3} px={4}>
							{localization.inventoryStatus.NotInAnyStore.t()}
						</Box>
					) : null}
					{availability.stores?.map((stockBalanceStore: any) => (
						<Box key={stockBalanceStore.storeName}>
							<Box py={3} px={4}>
								<Butik stockBalanceStore={stockBalanceStore} key={stockBalanceStore.storeName} />
							</Box>
							<Divider />
						</Box>
					))}
				</Stack>
			</Drawer>
		</>
	);
};

const Butik = ({ stockBalanceStore }: any) => {
	const localization = useLocalization('CommerceEnvironment');
	return (
		<Stack spacing={1}>
			<Box>
				{stockBalanceStore?.link ? (
					<Linkable href={stockBalanceStore.link}>
						<Typography fontSize="large" fontWeight={700} textTransform={'uppercase'}>
							{stockBalanceStore.storeName}
						</Typography>
					</Linkable>
				) : (
					<Typography fontSize="large" fontWeight={700} textTransform={'uppercase'}>
						{stockBalanceStore.storeName}
					</Typography>
				)}
				{stockBalanceStore.concept ? (
					<Typography fontSize="small">{stockBalanceStore.concept}</Typography>
				) : null}
				{stockBalanceStore.address ? (
					<Typography fontSize="small">{`${stockBalanceStore.address}, ${stockBalanceStore.zip} ${stockBalanceStore.city}`}</Typography>
				) : null}
			</Box>
			{stockBalanceStore.stock.stockLevel > 0 ? (
				<Stack direction={'row'} spacing={0.5}>
					<CircleTwoToneIcon fontSize="small" sx={{ color: colors.green[700] }} />
					<Box>
						<Typography>{localization.inventoryStatus.Available.t()}</Typography>
						<Typography>{`${stockBalanceStore.stock.stockLevel} ${stockBalanceStore.stock.unit}`}</Typography>
					</Box>
				</Stack>
			) : (
				<Stack direction={'row'} spacing={0.5}>
					<CircleTwoToneIcon fontSize="small" sx={{ color: colors.yellow[700] }} />
					<Box>
						<Typography>{localization.inventoryStatus.OrderItem.t()}</Typography>
						<Typography>
							{localization.inventoryStatus.LeadTime.t({
								leadTime: stockBalanceStore.stock.leadTime,
							})}
						</Typography>
					</Box>
				</Stack>
			)}
		</Stack>
	);
};
